import {
  createEnterpriseResponseSchema,
  deleteEnterpriseResponseSchema,
  duplicateEnterpriseResponseSchema,
  enterpriseDuplicateJobResponseSchema,
  enterpriseSummarySchema,
  getEnterprisesResponseSchema,
  type DuplicateEnterpriseBody,
  type GetEnterprisesResponse,
  UpdateEnterpriseSettingsBody,
} from "@mg/schemas/src/christo/catalyst";

import { API_URL } from "../config/env";
import { pollUntilSettled } from "../utils/enterpriseDuplicate";
import { smartFetch, handleApiError } from "../utils/http";

export type CreateEnterpriseInput = {
  name: string;
  isDemo?: boolean;
};

export type DuplicateEnterpriseInput = DuplicateEnterpriseBody;

export async function duplicateEnterprise(
  sourceEnterpriseId: string,
  body: DuplicateEnterpriseInput,
) {
  const requestUrl = `${API_URL}/private/catalyst/internal/enterprises/${encodeURIComponent(sourceEnterpriseId)}/duplicate`;
  const json = await smartFetch(requestUrl, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return duplicateEnterpriseResponseSchema.parse(json);
}

export async function getEnterpriseDuplicateJob(jobId: string) {
  const requestUrl = `${API_URL}/private/catalyst/internal/enterprise-duplicate-jobs/${encodeURIComponent(jobId)}`;
  const json = await smartFetch(requestUrl);
  return enterpriseDuplicateJobResponseSchema.parse(json);
}

export async function duplicateEnterpriseAndWait(
  sourceEnterpriseId: string,
  body: DuplicateEnterpriseInput,
) {
  const enqueued = await duplicateEnterprise(sourceEnterpriseId, body);
  const job = await pollUntilSettled(async () => {
    const status = await getEnterpriseDuplicateJob(enqueued.jobId);
    if (status.status === "failed") {
      throw new Error(status.errorMessage ?? "Duplicate job failed");
    }
    return status;
  });
  return {
    ...enqueued,
    ...job,
    targetEnterpriseId: job.targetEnterpriseId ?? enqueued.targetEnterpriseId,
  };
}

export async function createEnterprise(input: CreateEnterpriseInput | string) {
  const body =
    typeof input === "string"
      ? { name: input }
      : { name: input.name, isDemo: input.isDemo === true };
  const requestUrl = `${API_URL}/private/catalyst/internal/enterprises`;
  const json = await smartFetch(requestUrl, {
    method: "POST",
    body: JSON.stringify(body),
  });

  return createEnterpriseResponseSchema.parse(json);
}

export async function deleteEnterprise(
  enterpriseId: string,
  options?: { cascade?: boolean },
) {
  const cascadeQuery = options?.cascade === true ? "?cascade=true" : "";
  const requestUrl = `${API_URL}/private/catalyst/internal/enterprises/${encodeURIComponent(enterpriseId)}${cascadeQuery}`;
  const json = await smartFetch(requestUrl, {
    method: "DELETE",
  });
  return deleteEnterpriseResponseSchema.parse(json);
}

export type UpdateEnterpriseInput = {
  isDemo?: boolean;
  name?: string;
  slug?: string;
};

export async function updateEnterprise(
  enterpriseId: string,
  updates: UpdateEnterpriseInput,
) {
  const requestUrl = `${API_URL}/private/catalyst/internal/enterprises/${encodeURIComponent(enterpriseId)}`;
  const json = await smartFetch(requestUrl, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
  return enterpriseSummarySchema.parse(json);
}

export async function updateEnterpriseIsDemo(
  enterpriseId: string,
  isDemo: boolean,
) {
  return updateEnterprise(enterpriseId, { isDemo });
}

export async function getEnterprises(): Promise<GetEnterprisesResponse> {
  const requestUrl = `${API_URL}/private/catalyst/enterprises`;
  const json = await smartFetch(requestUrl);
  return getEnterprisesResponseSchema.parse(json);
}

export async function updateEnterpriseSettings(
  payload: UpdateEnterpriseSettingsBody,
) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/enterprises/settings`,
    {
      method: "PUT",
      body: JSON.stringify({
        ...payload,
      }),
    },
  );

  const parsedData = createEnterpriseResponseSchema.parse(json);
  return parsedData;
}

export async function getIntegrationSettings() {
  const requestUrl = `${API_URL}/private/catalyst/enterprises/integrations`;
  const json = await smartFetch(requestUrl);
  return json;
}

export async function updateIntegrationSettings(data: Record<string, any>) {
  try {
    const json = await smartFetch(
      `${API_URL}/private/catalyst/enterprises/integrations`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return json;
  } catch (e) {
    return handleApiError(e);
  }
}

export async function getIntegrationHealth() {
  try {
    return await smartFetch(
      `${API_URL}/private/catalyst/enterprises/integrations/health`,
    );
  } catch (e) {
    return handleApiError(e);
  }
}

export async function triggerIntegrationTest(params: {
  platform: string;
  taskId: string;
}) {
  try {
    return await smartFetch(
      `${API_URL}/private/catalyst/enterprises/integrations/test`,
      {
        method: "POST",
        body: JSON.stringify(params),
      },
    );
  } catch (e) {
    return handleApiError(e);
  }
}

export async function getIntegrationEventStatus(compositeTaskId: string) {
  try {
    return await smartFetch(
      `${API_URL}/private/catalyst/enterprises/integrations/events/${encodeURIComponent(compositeTaskId)}`,
    );
  } catch (e) {
    return handleApiError(e);
  }
}

export async function getRecentIntegrationTasks(platform: string) {
  return smartFetch(
    `${API_URL}/private/catalyst/enterprises/integrations/recent-tasks/${encodeURIComponent(platform)}`,
  );
}

export async function getAsanaProjects() {
  return smartFetch(
    `${API_URL}/private/catalyst/enterprises/integrations/asana/projects`,
  );
}

export async function registerAsanaWebhook(params: {
  projectGid: string;
  projectName: string;
}) {
  return smartFetch(
    `${API_URL}/private/catalyst/enterprises/integrations/asana/webhooks`,
    {
      method: "POST",
      body: JSON.stringify(params),
    },
  );
}

export async function removeAsanaWebhook(webhookId: string) {
  return smartFetch(
    `${API_URL}/private/catalyst/enterprises/integrations/asana/webhooks/${encodeURIComponent(webhookId)}`,
    { method: "DELETE" },
  );
}

// @ts-expect-error TS2339: `window` is strictly typed
window.UnsafeDeps = Object.assign({}, window.UnsafeDeps, {
  enterpriseApis: {
    createEnterprise,
    duplicateEnterprise,
    getEnterpriseDuplicateJob,
    getEnterprises,
    updateEnterpriseSettings,
    deleteEnterprise,
    updateEnterpriseIsDemo,
  },
});
